// client/components/AddressAutocomplete.jsx
import React, { useEffect, useRef, useState } from "react";
import Classes from "../Information.module.css";

function loadGooglePlaces(apiKey) {
  if (!apiKey) return Promise.resolve(null);
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  return new Promise((resolve, reject) => {
    const existing = document.getElementById("gmaps-script");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.google));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.id = "gmaps-script";
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.onload = () => resolve(window.google);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

export default function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Start typing an address…",
  className = "",
  apiKey = "",
  country,
}) {
  const [ready, setReady] = useState(false);
  const [useGoogle, setUseGoogle] = useState(Boolean(apiKey));
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const g = await loadGooglePlaces(apiKey);
        if (!mounted) return;
        if (g && g.maps?.places) {
          setUseGoogle(true);
          setReady(true);
        } else {
          setUseGoogle(false);
          setReady(true);
        }
      } catch {
        setUseGoogle(false);
        setReady(true);
      }
    })();
    return () => { mounted = false; };
  }, [apiKey]);

  useEffect(() => {
    if (!ready || !value) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;

    const fetchGoogle = () => {
      const svc = new window.google.maps.places.AutocompleteService();
      const req = { input: value, types: ["address"] };
      if (country) req.componentRestrictions = { country };
      svc.getPlacePredictions(req, (preds, status) => {
        if (cancelled) return;
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !preds) {
          setSuggestions([]);
          return;
        }
        setSuggestions(preds.map((p) => ({
          id: p.place_id,
          primary: p.structured_formatting?.main_text || p.description,
          secondary: p.structured_formatting?.secondary_text || "",
          full: p.description,
          placeId: p.place_id,
        })));
      });
    };

    const fetchOSM = async () => {
      try {
        const url = new URL("https://nominatim.openstreetmap.org/search");
        url.searchParams.set("q", value);
        url.searchParams.set("format", "json");
        url.searchParams.set("addressdetails", "1");
        url.searchParams.set("limit", "6");
        const res = await fetch(url.toString(), { headers: { "Accept-Language": "en" } });
        const data = await res.json();
        if (cancelled) return;
        setSuggestions(data.map((d) => ({
          id: d.place_id,
          primary: d.display_name.split(",")[0] || d.display_name,
          secondary: d.display_name.split(",").slice(1).join(", ")?.trim(),
          full: d.display_name,
          osm: d,
        })));
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    };

    if (useGoogle && window.google?.maps?.places) fetchGoogle();
    else fetchOSM();
    return () => { cancelled = true; };
  }, [ready, value, useGoogle, country]);

  const selectItem = (item) => {
    setOpen(false);
    setHighlight(-1);

    if (useGoogle && item.placeId) {
      const svc = new window.google.maps.places.PlacesService(document.createElement("div"));
      svc.getDetails(
        {
          placeId: item.placeId,
          fields: ["formatted_address", "address_components", "geometry", "place_id"],
        },
        (place, status) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
            onChange(item.full);
            onSelect?.({ formatted: item.full });
            return;
          }
          const components = parseGoogleComponents(place.address_components || []);
          const lat = place.geometry?.location?.lat?.();
          const lng = place.geometry?.location?.lng?.();
          onChange(place.formatted_address || item.full);
          onSelect?.({
            formatted: place.formatted_address || item.full,
            components,
            lat,
            lng,
            placeId: place.place_id,
            provider: "google",
          });
        }
      );
    } else {
      const d = item.osm;
      const components = {
        line1: [d.address?.house_number, d.address?.road].filter(Boolean).join(" ")?.trim() || "",
        city: d.address?.city || d.address?.town || d.address?.village || d.address?.hamlet || "",
        state: d.address?.state || "",
        postalCode: d.address?.postcode || "",
        country: d.address?.country || "",
      };
      const lat = d.lat ? Number(d.lat) : undefined;
      const lng = d.lon ? Number(d.lon) : undefined;
      onChange(item.full);
      onSelect?.({ formatted: item.full, components, lat, lng, provider: "osm" });
    }
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown")       { e.preventDefault(); setHighlight((h) => Math.min(h + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp")    { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); selectItem(suggestions[highlight]); }
    else if (e.key === "Escape")     { setOpen(false); }
  };

  useEffect(() => {
    const handle = (e) => {
      if (!listRef.current || listRef.current.contains(e.target) || inputRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  return (
    <div className={Classes.addressWrap}>
      <input
        ref={inputRef}
        type="text"
        className={`${Classes.inputField} ${className || ""}`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => value && setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />

      {open && suggestions.length > 0 && (
        <div ref={listRef} className={Classes.suggestBox} role="listbox">
          {suggestions.map((s, i) => (
            <button
              key={s.id}
              type="button"
              className={`${Classes.suggestItem} ${i === highlight ? Classes.suggestItemActive : ""}`}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectItem(s)}
              role="option"
              aria-selected={i === highlight}
            >
              <div className={Classes.suggestPrimary}>{s.primary}</div>
              {s.secondary ? <div className={Classes.suggestSecondary}>{s.secondary}</div> : null}
            </button>
          ))}
          <div className={Classes.suggestFooter}>
            <span className={Classes.poweredBy}>
              {useGoogle ? 'Powered by Google' : 'Search by OpenStreetMap'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function parseGoogleComponents(components) {
  const get = (type) => components.find((c) => c.types?.includes(type)) || {};
  const num = get("street_number")?.long_name || "";
  const route = get("route")?.long_name || "";
  return {
    line1: [num, route].filter(Boolean).join(" ")?.trim(),
    city: get("locality")?.long_name || get("sublocality")?.long_name || get("postal_town")?.long_name || "",
    state: get("administrative_area_level_1")?.short_name || get("administrative_area_level_1")?.long_name || "",
    postalCode: get("postal_code")?.long_name || "",
    country: get("country")?.long_name || "",
  };
}
